import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BaseElement, customElement, SlotObserver } from '../base-element';
import imageStyles from './image.scss?inline';

@customElement('ps-image')
export class ImageWC extends SlotObserver(BaseElement, [
  '[slot="fallbackImage"]',
]) {
  static styles = unsafeCSS(imageStyles);

  /** Image width. */
  @property({ reflect: true }) width?: string;

  /** Image height. */
  @property({ reflect: true }) height?: string;

  /** Image border radius. */
  @property({ reflect: true }) radius: 'small' | 'medium' = 'medium';

  /** Alt tag for image. */
  @property({ reflect: true }) alt = '';

  /** Image src. */
  @property({ reflect: true }) src = '';

  /**
   * Load failure fault-tolerant src
   */
  @property({ type: String }) fallback?: string = '';

  /** Image srcset. */
  @property({ reflect: true }) srcset?: string;

  /** Image sizes. */
  @property({ reflect: true }) sizes?: string;

  /** Image lazyload. */
  @property({ reflect: true, type: Boolean }) lazyload = true;

  @property({ type: Boolean }) private _hasError = false;

  @property({ reflect: true }) 'data-testid': string;

  @property({ reflect: true }) referrerpolicy:
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url' = 'no-referrer';

  private _onError() {
    if (this.fallback) {
      this.src = this.fallback;
    } else {
      this.src = '';
    }

    this._hasError = true;
    this.emit('onerror', { detail: { hasError: this._hasError } });
  }

  render() {
    let aspectRatio;

    if (Number(this.width) && Number(this.height)) {
      aspectRatio = Number(this.width) / Number(this.height);
    } else if (this.width?.includes('px') && this.height?.includes('px')) {
      aspectRatio =
        Number(this.width?.replace('px', '')) /
        Number(this.height?.replace('px', ''));
    }

    if (
      (this._hasError || !this.src) &&
      this.hasSlottedContent('[slot="fallbackImage"]')
    ) {
      return html`<slot name="fallbackImage"></slot> `;
    }

    return html`
      <img
        class=${classMap({
          'c-image': true,
          [`c-image--radius-${this.radius}`]: this.radius,
        })}
        width="${ifDefined(this.width)}"
        height="${ifDefined(this.height)}"
        alt="${this.alt}"
        src="${this.src}"
        srcset="${ifDefined(this.srcset)}"
        sizes="${ifDefined(this.sizes)}"
        loading="${this.lazyload ? 'lazy' : 'eager'}"
        referrerpolicy="${this.referrerpolicy}"
        @error="${this._onError}"
        style="--image-ratio: ${aspectRatio}"
      />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-image': ImageWC;
  }
  enum PSElementTagNameMap {
    'ps-image' = 'ps-image',
  }
}
